/**
 * ポータル: 補償休日（台帳方式）
 * 付与（＋）・取得（−）を明細で記録し、人ごとの残日数を一覧する。
 * 付与の自動計算は会社規程の差が大きいため、v1は手動登録（0.5日刻み）。
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarDays, Plus, Trash2 } from 'lucide-react';
import * as api from '../../services/api';
import { PV, Avatar } from './PortalLayout';
import { Modal, fieldStyle, labelStyle, fmtDateTime, EmptyState } from './ui';

interface SummaryRow { user_id: string; user_name: string; user_role: string; granted: number; used: number; balance: number; last_date: string | null }
interface Entry { id: string; entry_date: string; days: number; reason: string | null; created_by_name: string | null; created_at: string }
interface CompRule { per_days: number; grant_days: number; rounding: 'floor1' | 'half05' | 'ceil1' }
interface CalcRow { user_id: string; user_name: string; onboard_days: number; suggested: number; granted_in_period: number }

const fmtDays = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));
const ROUNDING_LABEL: Record<string, string> = { floor1: '1日未満 切り捨て', half05: '0.5日単位 四捨五入', ceil1: '1日単位 切り上げ' };

export default function PortalCompDaysPage() {
  const user = useMemo(() => { try { return JSON.parse(localStorage.getItem('admin_user') ?? '{}'); } catch { return {}; } }, []);
  const isAdmin = ['system_admin', 'shore_admin'].includes(user?.user_role ?? '');

  const [rows, setRows] = useState<SummaryRow[]>([]);
  const [open, setOpen] = useState<SummaryRow | null>(null);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [showAdd, setShowAdd] = useState<SummaryRow | null>(null);
  const [showRule, setShowRule] = useState(false);
  const [showCalc, setShowCalc] = useState(false);
  const [rule, setRule] = useState<CompRule | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const r = await api.get<{ data: SummaryRow[] }>('/portal/comp-days'); setRows(r.data ?? []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    api.get<{ data: CompRule }>('/portal/comp-days/settings').then(r => setRule(r.data)).catch(() => { /* noop */ });
  }, []);

  const openEntries = async (r: SummaryRow) => {
    setOpen(r);
    try { const res = await api.get<{ data: Entry[] }>(`/portal/comp-days/${r.user_id}/entries`); setEntries(res.data ?? []); }
    catch { setEntries([]); }
  };

  return (
    <div className="page-wrap">
      <div className="top-bar">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CalendarDays size={20} /> 補償休日
        </h1>
        {isAdmin && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" onClick={() => setShowRule(true)}>計算ルール</button>
            <button className="btn-p" onClick={() => setShowCalc(true)}>乗船日数から計算</button>
          </div>
        )}
      </div>

      <div className="card" style={{ padding: '12px 16px', marginBottom: 14, fontSize: 13, color: PV.textSub, lineHeight: 1.7 }}>
        乗船中に与えられなかった休日の<strong style={{ color: PV.text }}>付与（＋）</strong>と<strong style={{ color: PV.text }}>取得（−）</strong>を記録し、残日数を管理します。
        法定の基準（船員法第62条）は「<strong style={{ color: PV.text }}>休日が取れなかった1週につき1日</strong>（週40時間の超過分は8時間につき1日・付与単位は原則1日）」です。
        {rule && <>現在の計算ルール: <strong style={{ color: PV.text }}>乗船{rule.per_days}日につき{fmtDays(rule.grant_days)}日・{ROUNDING_LABEL[rule.rounding]}</strong>（「計算ルール」で会社の規程に合わせて変更できます）。</>}
        船員本人はスマホの「メニュー」から自分の残日数を確認できます。
      </div>

      <div className="card" style={{ padding: 0, overflow: 'auto' }}>
        {loading ? <div className="loading">読み込み中...</div> : rows.length === 0 ? (
          <EmptyState img="/portal/empty-requests.png" text="対象者がいません" />
        ) : (
          <table>
            <thead><tr>
              <th>氏名</th><th style={{ width: 110, textAlign: 'right' }}>付与 計</th>
              <th style={{ width: 110, textAlign: 'right' }}>取得 計</th>
              <th style={{ width: 120, textAlign: 'right' }}>残日数</th>
              <th style={{ width: 120 }}>最終記録日</th>
              {isAdmin && <th style={{ width: 150 }} />}
            </tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.user_id} style={{ cursor: 'pointer' }} onClick={() => void openEntries(r)}>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9 }}>
                      <Avatar name={r.user_name} size={28} />
                      <span style={{ fontWeight: 700 }}>{r.user_name}</span>
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>{fmtDays(r.granted)}日</td>
                  <td style={{ textAlign: 'right' }}>{fmtDays(r.used)}日</td>
                  <td style={{ textAlign: 'right' }}>
                    <span style={{ fontWeight: 800, fontSize: 15, color: r.balance > 0 ? PV.accentDark : r.balance < 0 ? '#d93025' : PV.textSub }}>
                      {fmtDays(r.balance)}日
                    </span>
                  </td>
                  <td style={{ fontSize: 12.5 }}>{r.last_date?.replace(/-/g, '/') ?? '—'}</td>
                  {isAdmin && (
                    <td onClick={e => e.stopPropagation()}>
                      <button className="btn-p" style={{ padding: '6px 12px', fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 5 }}
                        onClick={() => setShowAdd(r)}>
                        <Plus size={13} /> 付与・取得
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {open && (
        <Modal width={620} title={`${open.user_name} さんの補償休日（残 ${fmtDays(open.balance)}日）`} onClose={() => setOpen(null)}>
          {entries.length === 0 ? (
            <div className="empty" style={{ padding: 20 }}>記録はまだありません</div>
          ) : (
            <table>
              <thead><tr><th style={{ width: 100 }}>基準日</th><th style={{ width: 90, textAlign: 'right' }}>日数</th><th>理由</th><th style={{ width: 130 }}>登録</th>{isAdmin && <th style={{ width: 50 }} />}</tr></thead>
              <tbody>
                {entries.map(e => (
                  <tr key={e.id}>
                    <td style={{ fontSize: 12.5 }}>{e.entry_date.replace(/-/g, '/')}</td>
                    <td style={{ textAlign: 'right', fontWeight: 800, color: e.days > 0 ? PV.accentDark : '#b45309' }}>
                      {e.days > 0 ? `+${fmtDays(e.days)}` : fmtDays(e.days)}日
                    </td>
                    <td style={{ fontSize: 13 }}>{e.reason ?? ''}</td>
                    <td style={{ fontSize: 11.5, color: PV.textSub }}>{e.created_by_name}<br />{fmtDateTime(e.created_at)}</td>
                    {isAdmin && (
                      <td>
                        <button className="btn" style={{ padding: '4px 8px' }} onClick={async () => {
                          if (!confirm('この明細を取り消しますか？')) return;
                          try {
                            await api.del(`/portal/comp-days/${e.id}`);
                            await openEntries(open); void load();
                          } catch (err: unknown) { alert(err instanceof Error ? err.message : '失敗しました'); }
                        }}><Trash2 size={13} color="#d93025" /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {isAdmin && (
            <div style={{ marginTop: 14 }}>
              <button className="btn-p" onClick={() => { setShowAdd(open); }}>この人に付与・取得を登録</button>
            </div>
          )}
        </Modal>
      )}

      {showAdd && (
        <AddModal target={showAdd} onClose={() => setShowAdd(null)}
          onSaved={async () => {
            setShowAdd(null);
            await load();
            if (open) { const latest = rows.find(r => r.user_id === open.user_id); await openEntries(latest ?? open); }
          }} />
      )}

      {showRule && rule && (
        <RuleModal rule={rule} onClose={() => setShowRule(false)}
          onSaved={r => { setRule(r); setShowRule(false); }} />
      )}

      {showCalc && (
        <CalcModal onClose={() => setShowCalc(false)}
          onRegistered={() => { setShowCalc(false); void load(); }} />
      )}
    </div>
  );
}

function RuleModal({ rule, onClose, onSaved }: { rule: CompRule; onClose: () => void; onSaved: (r: CompRule) => void }) {
  const [per, setPer] = useState(String(rule.per_days));
  const [grant, setGrant] = useState(String(rule.grant_days));
  const [rounding, setRounding] = useState<string>(rule.rounding);
  const [busy, setBusy] = useState(false);
  return (
    <Modal width={480} title="補償休日の計算ルール（会社マスタ）" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 12.5, color: PV.textSub, lineHeight: 1.7 }}>
          自動計算の式: <strong style={{ color: PV.text }}>付与日数 ＝ 乗船日数 ÷ ①日 × ②日</strong>（③の方法で丸め）。<br />
          船員法第62条の最低基準（休日なし1週につき1日）に相当する既定値は「7日につき1日・1日未満切り捨て」です。
          労使協定・就業規則で上乗せがある場合はここで調整してください。
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>① 乗船何日につき</label>
            <input type="number" min={1} max={90} style={fieldStyle} value={per} onChange={e => setPer(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>② 付与日数</label>
            <input type="number" step="0.5" min={0.5} style={fieldStyle} value={grant} onChange={e => setGrant(e.target.value)} />
          </div>
        </div>
        <div>
          <label style={labelStyle}>③ 端数の扱い</label>
          <select style={fieldStyle} value={rounding} onChange={e => setRounding(e.target.value)}>
            <option value="floor1">1日未満 切り捨て（法定どおりの堅い運用）</option>
            <option value="half05">0.5日単位 四捨五入</option>
            <option value="ceil1">1日単位 切り上げ（船員に有利）</option>
          </select>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn" onClick={onClose}>キャンセル</button>
          <button className="btn-p" disabled={busy} onClick={async () => {
            setBusy(true);
            try {
              const r = await api.put<{ data: CompRule }>('/portal/comp-days/settings',
                { per_days: parseInt(per, 10), grant_days: parseFloat(grant), rounding });
              onSaved(r.data);
            } catch (e: unknown) { alert(e instanceof Error ? e.message : '保存に失敗しました'); }
            finally { setBusy(false); }
          }}>{busy ? '保存中...' : '保存する'}</button>
        </div>
      </div>
    </Modal>
  );
}

function CalcModal({ onClose, onRegistered }: { onClose: () => void; onRegistered: () => void }) {
  // 既定は先月（1日〜末日）
  const now = new Date(Date.now() + 9 * 3600 * 1000);
  const firstPrev = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  const lastPrev = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
  const [from, setFrom] = useState(firstPrev.toISOString().slice(0, 10));
  const [to, setTo] = useState(lastPrev.toISOString().slice(0, 10));
  const [calcRows, setCalcRows] = useState<CalcRow[] | null>(null);
  const [ruleUsed, setRuleUsed] = useState<CompRule | null>(null);
  const [days, setDays] = useState<Record<string, string>>({});
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);

  const calc = async () => {
    setBusy(true);
    try {
      const r = await api.post<{ data: { rule: CompRule; rows: CalcRow[] } }>('/portal/comp-days/calc', { date_from: from, date_to: to });
      setCalcRows(r.data.rows);
      setRuleUsed(r.data.rule);
      const d: Record<string, string> = {}; const c: Record<string, boolean> = {};
      for (const row of r.data.rows) {
        d[row.user_id] = String(row.suggested);
        c[row.user_id] = row.suggested > 0 && row.granted_in_period === 0;
      }
      setDays(d); setChecked(c);
    } catch (e: unknown) { alert(e instanceof Error ? e.message : '計算に失敗しました'); }
    finally { setBusy(false); }
  };

  const register = async () => {
    const entries = (calcRows ?? [])
      .filter(r => checked[r.user_id] && parseFloat(days[r.user_id] ?? '0') > 0)
      .map(r => ({
        user_id: r.user_id, days: parseFloat(days[r.user_id] ?? '0'), entry_date: to,
        reason: `${from.replace(/-/g, '/')}〜${to.replace(/-/g, '/')} 乗船分の付与（自動計算: 乗船${r.onboard_days}日）`,
      }));
    if (entries.length === 0) { alert('登録対象を選んでください'); return; }
    if (!confirm(`${entries.length}人に補償休日を付与します。よろしいですか？`)) return;
    setBusy(true);
    try {
      await api.post('/portal/comp-days/bulk', { entries });
      onRegistered();
    } catch (e: unknown) { alert(e instanceof Error ? e.message : '登録に失敗しました'); }
    finally { setBusy(false); }
  };

  return (
    <Modal width={720} title="乗船日数から補償休日を計算（目安→確認して付与）" onClose={onClose}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 12, flexWrap: 'wrap' }}>
        <div>
          <label style={labelStyle}>期間（開始）</label>
          <input type="date" style={fieldStyle} value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>期間（終了）</label>
          <input type="date" style={fieldStyle} value={to} onChange={e => setTo(e.target.value)} />
        </div>
        <button className="btn-p" disabled={busy} onClick={() => void calc()}>{busy ? '計算中...' : '計算する'}</button>
        {ruleUsed && (
          <span style={{ fontSize: 12, color: PV.textSub }}>
            ルール: 乗船{ruleUsed.per_days}日につき{fmtDays(ruleUsed.grant_days)}日・{ROUNDING_LABEL[ruleUsed.rounding]}
          </span>
        )}
      </div>

      {calcRows && (
        calcRows.length === 0 ? (
          <div className="empty" style={{ padding: 20 }}>この期間に乗船記録のある人はいません</div>
        ) : (
          <>
            <table>
              <thead><tr>
                <th style={{ width: 40 }} /><th>氏名</th>
                <th style={{ width: 100, textAlign: 'right' }}>乗船日数</th>
                <th style={{ width: 100, textAlign: 'right' }}>目安</th>
                <th style={{ width: 110, textAlign: 'right' }}>期間内付与済</th>
                <th style={{ width: 110 }}>付与日数</th>
              </tr></thead>
              <tbody>
                {calcRows.map(r => (
                  <tr key={r.user_id}>
                    <td><input type="checkbox" checked={!!checked[r.user_id]} onChange={e => setChecked(c => ({ ...c, [r.user_id]: e.target.checked }))} /></td>
                    <td style={{ fontWeight: 700 }}>{r.user_name}</td>
                    <td style={{ textAlign: 'right' }}>{r.onboard_days}日</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: PV.accentDark }}>{fmtDays(r.suggested)}日</td>
                    <td style={{ textAlign: 'right', color: r.granted_in_period > 0 ? '#b45309' : PV.textSub }}>{fmtDays(r.granted_in_period)}日</td>
                    <td>
                      <input type="number" step="0.5" min="0" style={{ ...fieldStyle, padding: '6px 8px' }}
                        value={days[r.user_id] ?? ''} onChange={e => setDays(d => ({ ...d, [r.user_id]: e.target.value }))} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ fontSize: 12, color: PV.textSub, margin: '10px 0' }}>
              「期間内付与済」がある人は二重付与にご注意ください（チェックは自動で外しています）。基準日は期間の終了日で登録されます。
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn" onClick={onClose}>閉じる</button>
              <button className="btn-p" disabled={busy} onClick={() => void register()}>チェックした人に付与する</button>
            </div>
          </>
        )
      )}
    </Modal>
  );
}

function AddModal({ target, onClose, onSaved }: { target: SummaryRow; onClose: () => void; onSaved: () => void }) {
  const [kind, setKind] = useState<'grant' | 'use'>('grant');
  const [days, setDays] = useState('1');
  const [date, setDate] = useState(new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  return (
    <Modal width={460} title={`${target.user_name} さん：補償休日の登録`} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {([['grant', '付与（＋）'], ['use', '取得（−）']] as const).map(([k, l]) => (
            <button key={k} onClick={() => setKind(k)}
              style={{
                flex: 1, padding: '11px 0', borderRadius: 9, cursor: 'pointer', fontSize: 14, fontWeight: 800, fontFamily: 'inherit',
                border: kind === k ? `2px solid ${PV.accent}` : '1px solid #c8cdd3',
                background: kind === k ? PV.accentSoft : '#fff', color: kind === k ? PV.accentDark : PV.textSub,
              }}>{l}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>日数（0.5日刻み）</label>
            <input type="number" step="0.5" min="0.5" style={fieldStyle} value={days} onChange={e => setDays(e.target.value)} />
          </div>
          <div style={{ flex: 1.3 }}>
            <label style={labelStyle}>基準日</label>
            <input type="date" style={fieldStyle} value={date} onChange={e => setDate(e.target.value)} />
          </div>
        </div>
        <div>
          <label style={labelStyle}>理由（例: 9月乗船分の付与 ／ 下船休暇で取得）</label>
          <input style={fieldStyle} value={reason} onChange={e => setReason(e.target.value)} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn" onClick={onClose}>キャンセル</button>
          <button className="btn-p" disabled={busy} onClick={async () => {
            const d = Math.abs(parseFloat(days));
            if (!Number.isFinite(d) || d <= 0) { alert('日数を入力してください'); return; }
            setBusy(true);
            try {
              await api.post('/portal/comp-days', {
                user_id: target.user_id, entry_date: date,
                days: kind === 'grant' ? d : -d, reason: reason || null,
              });
              onSaved();
            } catch (e: unknown) { alert(e instanceof Error ? e.message : '登録に失敗しました'); }
            finally { setBusy(false); }
          }}>{busy ? '登録中...' : '登録する'}</button>
        </div>
      </div>
    </Modal>
  );
}
